<template>
  <EditorModal
    :title="listName || t('listSegmentation.title')"
    :toolbar-right-buttons="toolbarRightButtons"
  >
    <template #content>
      <!-- Options bar -->
      <div class="options-bar">
        <!-- Keep updated option -->
        <div class="keep-updated-option">
          <InputSwitch
            v-model="keepListUpdated"
            input-id="keepListUpdated"
            class="keep-updated-switch"
          />
          <label
            for="keepListUpdated"
            class="keep-updated-label"
          >
            {{ t('listSegmentation.settings.keepUpdated') }}
          </label>
          <i
            v-tooltip.top="t('listSegmentation.settings.keepUpdatedTip')"
            class="far fa-circle-question keep-updated-help"
          />
        </div>

        <!-- Contacts count -->
        <div class="contacts-count">
          <span class="contacts-count-label">{{ t('segmentation.totalContactsLabel') }}</span>
          <span
            v-if="!loadingContactsCount"
            class="contacts-count-value"
          >
            {{ totalContactsCount !== null ? formatNumbersInString(totalContactsCount.toString()) : 'N/A' }}
          </span>
          <Skeleton
            v-else
            height="0.875rem"
            width="2rem"
          />
          <i
            v-tooltip.top="t('listSegmentation.refreshCount')"
            class="far fa-arrows-rotate contacts-count-refresh"
            :class="{ 'fa-spin': loadingContactsCount }"
            @click="handleRefreshContactsCount"
          />
        </div>
      </div>

      <!-- Segmentation content -->
      <div class="container-fluid py-3 mx-auto">
        <SegmentationsPanel
          v-if="segmentsConfiguration !== null"
          v-model:model-value="segmentsConfiguration"
          :show-prompt-a-i="true"
          :trigger-count="triggerCount"
          origin="list"
          @count-updated="handleCountUpdated"
        />
      </div>
    </template>
  </EditorModal>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import EditorModal from '@/components/modals/EditorModal.vue';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
import InputSwitch from 'primevue/inputswitch';
import Skeleton from 'primevue/skeleton';
import { store } from '@/store';
import { ListType } from '@/types';
import { showToastError, showToastSuccess } from '@/helpers';
import { formatNumbersInString } from '@/helpers/Number';
import { NestApiType, nestCall } from '@/composables/nestApi';

export default defineComponent({
  name: 'ListSegmentationEditor',

  components: {
    EditorModal,
    SegmentationsPanel,
    InputSwitch,
    Skeleton,
  },

  props: {
    idShopList: {
      type: Number,
      required: true,
    },
  },

  emits: ['close', 'saved'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const router = useRouter();
    const segmentsConfiguration: any = ref(null);
    const getInitialized: any = computed(() => store.getters['segmentationsEditor/getInitialized']);

    // Local state
    const listName = ref('');
    const keepListUpdated = ref(false);
    const loadingContactsCount = ref(false);
    const totalContactsCount = ref<number | null>(null);
    const triggerCount = ref(0);

    /**
     * Fetch list details from API using nestCall
     */
    const fetchListDetails = async (): Promise<{ name: string; type: string; segmentation_config: string; keep_updated: boolean } | null> => {
      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          `/lists/${props.idShopList}`,
          {},
          '',
          undefined,
          true,
          { fields: ['name', 'type', 'segmentation_config', 'keep_updated'] },
        );

        if (response && response.statusCode === 200 && response.data.name
          && response.data.type === ListType.SEGMENT
        ) {
          return {
            name: response.data.name,
            type: response.data.type,
            segmentation_config: response.data.segmentation_config,
            keep_updated: response.data.keep_updated,

          };
        }
        return null;
      } catch (error) {
        console.error('Error fetching list details:', error);
        return null;
      }
    };

    /**
     * Handle count update from SegmentationsPanel
     */
    const handleCountUpdated = (data: { count: number | null; loading: boolean }) => {
      loadingContactsCount.value = data.loading;
      if (!data.loading) {
        totalContactsCount.value = data.count;
      }
    };

    /**
     * Request count refresh from SegmentationsPanel
     */
    const handleRefreshContactsCount = () => {
      loadingContactsCount.value = true;
      triggerCount.value += 1;
    };

    /**
     * Initialize the segmentation editor
     */
    const initializeEditor = async () => {
      // Fetch list details (includes type check for segment)
      const listDetails = await fetchListDetails();

      if (!listDetails) {
        await showToastError(t('listSegmentation.errors.notSegmentType'));
        router.push('/lists/manage');
        emit('close');
        return;
      }

      // Set values from API response
      listName.value = listDetails.name;
      segmentsConfiguration.value = JSON.parse(listDetails.segmentation_config || '[[{}]]');
      keepListUpdated.value = listDetails.keep_updated ?? false;
    };

    watch(getInitialized, () => {
      if (getInitialized.value) {
        handleRefreshContactsCount();
      }
    });

    /**
     * Close the editor without saving
     */
    const handleClose = () => {
      store.commit('segmentationsEditor/resetState');
      emit('close');
    };

    /**
     * Save the segmentation configuration
     */
    const handleSave = async (closeAfterSave = true) => {
      // Validate segmentation configuration
      await store.dispatch('segmentationsEditor/validate');
      const segmentationHasError = store.getters['segmentationsEditor/getHasError'];
      if (segmentationHasError) {
        await showToastError(t('listSegmentation.messages.validationError'));
        return;
      }

      store.commit('general/showTheSpinner');

      try {
        // Build the payload for the API
        const payload = {
          name: listName.value,
          segmentation_config: JSON.stringify(segmentsConfiguration.value || { conditions: [] }),
          keep_updated: keepListUpdated.value,
        };

        // Call NestJS API to update the list
        const response = await nestCall(
          'patch',
          NestApiType.CLIENT_API,
          `/lists/${props.idShopList}`,
          {},
          '',
          payload,
          true,
        );

        if (!response || response.statusCode) {
          const errorMessage = response?.message || t('listSegmentation.messages.error');
          await showToastError(errorMessage);
          return;
        }

        await showToastSuccess(t('listSegmentation.messages.saved'));
        emit('saved', props.idShopList);

        if (closeAfterSave) {
          handleClose();
        }
      } catch (error) {
        console.error('Error saving segmentation:', error);
        await showToastError(t('listSegmentation.messages.error'));
      } finally {
        store.commit('general/hideTheSpinner');
      }
    };

    // Toolbar configuration
    const toolbarRightButtons = computed(() => [
      {
        label: t('templateBuilder.save'),
        items: [
          {
            label: t('templateBuilder.saveAndLeave'),
            command: () => handleSave(true),
          },
          {
            label: t('templateBuilder.leaveWithoutSaving'),
            command: handleClose,
          },
        ],
      },
    ]);

    // Initialize on mount
    onMounted(async () => {
      await initializeEditor();
    });

    return {
      t,
      listName,
      segmentsConfiguration,
      keepListUpdated,
      toolbarRightButtons,
      loadingContactsCount,
      totalContactsCount,
      triggerCount,
      handleRefreshContactsCount,
      handleCountUpdated,
      formatNumbersInString,
    };
  },
});
</script>

<style scoped lang="scss">
.options-bar {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.7rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.keep-updated-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.keep-updated-switch {
  transform: scale(0.85);
}

.keep-updated-label {
  font-size: 0.8125rem;
  color: #495057;
  cursor: pointer;
  margin: 0;
  user-select: none;
}

.keep-updated-help {
  color: #adb5bd;
  cursor: help;
  font-size: 0.75rem;
  transition: color 0.15s ease;

  &:hover {
    color: #6c757d;
  }
}

.contacts-count {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: #6c757d;
}

.contacts-count-label {
  color: #6c757d;
}

.contacts-count-value {
  font-weight: 600;
  color: #212529;
}

.contacts-count-refresh {
  font-size: 0.75rem;
  color: #adb5bd;
  cursor: pointer;
  margin-left: 0.25rem;
  transition: color 0.15s ease;

  &:hover {
    color: #495057;
  }
}
</style>
